/**
 * Joins the `voice:<zone>:<instance>` Phoenix channel and runs a
 * LiveKit `Room` for the audio plane. The channel carries PTT intents
 * (voice_start / voice_stop) so World.Tick can compute proximity; the
 * audio bytes themselves flow over a separate `RTCPeerConnection`
 * straight to `livekit-server`.
 *
 * Browser autoplay policy requires a user gesture before any audio
 * plays — `start()` is called from inside the same gesture that opens
 * the mic.
 *
 * See [docs/guides/spatial-audio-livekit.md](../../../../docs/guides/spatial-audio-livekit.md).
 */

import type { Channel, Socket } from "phoenix";
import type { Room } from "livekit-client";
import { config } from "@/config";
import { activeJwt } from "@/auth/session";
import { openMic } from "./MicDiscovery";
import { MicStore } from "./MicStore";
import { LiveKitVoiceEngineSession } from "./LiveKitVoiceEngineSession";
import { SpatialAudioController } from "./SpatialAudioController";

export type VoiceMode = "say" | "shout" | "whisper";

export interface VoiceConnectionOptions {
  socket: Socket;
  zoneId: string;
  instance: string;
  /**
   * Notified whenever the server broadcasts a `voice_state` event on the
   * voice channel — i.e. someone in the zone pressed/released PTT.
   * Used by `PlayerList` to draw the 🎤 broadcasting icon next to
   * active speakers (including self).
   */
  onVoiceState?: (userId: string, active: boolean) => void;
  /**
   * Notified every `METER_INTERVAL_MS` while the user is speaking with
   * the mic's normalised RMS (0..1). Used by `VoiceMeter` for the
   * sidebar level bar. Only fires between `startSpeaking()` and
   * `stopSpeaking()`.
   */
  onLevel?: (rms: number) => void;
  /**
   * Notified when the VAD speaking state crosses the threshold while
   * the mic is open. Used by `VoiceMeter` to flash the meter and
   * update the mode label.
   */
  onSpeakingChange?: (speaking: boolean, mode: VoiceMode) => void;
}

const VAD_THRESHOLD = 0.02; // ~ -34 dBFS, below typical room noise floor
const METER_INTERVAL_MS = 60;

interface VoiceStatePush {
  user_id: string;
  active: boolean;
  mode: string;
  target: string | null;
  x: number;
  y: number;
  ts: string;
}

interface JoinReply {
  instance: number | string;
  user_id: string;
  audio_impl?: "stub" | "livekit" | "unknown";
}

interface LiveKitTokenReply {
  token: string;
  ws_url: string;
  room: string;
  ttl_seconds: number;
}

export class VoiceConnection {
  private socket: Socket;
  private zoneId: string;
  private instance: string;
  private onVoiceState: ((userId: string, active: boolean) => void) | undefined;
  private onLevel: (rms: number) => void;
  private onSpeakingChange: (speaking: boolean, mode: VoiceMode) => void;

  private channel: Channel | null = null;
  private session: LiveKitVoiceEngineSession | null = null;
  private spatialController: SpatialAudioController | null = null;
  private localStream: MediaStream | null = null;
  private speaking = false;
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshing = false;

  // Local-capture meter: an AnalyserNode tapped off the same
  // MediaStream LiveKit publishes. Drives the sidebar level bar +
  // VAD-derived speaking state. Only ticks while `speaking` is true
  // (between startSpeaking and stopSpeaking).
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private meterTimer: number | null = null;
  private currentMode: VoiceMode = "say";
  private vadSpeaking = false;

  /**
   * Lead time before token expiry to fetch + apply a fresh token.
   * 5 minutes — generous margin against clock skew and signaling
   * reconnect latency. With a 6h server TTL this fires once per
   * ~5:55 of session.
   */
  private static readonly REFRESH_LEAD_MS = 5 * 60 * 1000;

  constructor(opts: VoiceConnectionOptions) {
    this.socket = opts.socket;
    this.zoneId = opts.zoneId;
    this.instance = opts.instance;
    this.onVoiceState = opts.onVoiceState;
    this.onLevel = opts.onLevel ?? (() => {});
    this.onSpeakingChange = opts.onSpeakingChange ?? (() => {});
  }

  /** Returns the SpatialAudioController so SpatialMusicLayer + ZoneScene can route track subscriptions. */
  getSpatialController(): SpatialAudioController | null {
    return this.spatialController;
  }

  /**
   * Returns the connected LiveKit Room. ZoneScene hands this to
   * `LiveKitMusicBroadcaster` so the jukebox publishes onto the same
   * RTCPeerConnection as the voice mic. `null` until `start()` resolves
   * and `room.connect()` has succeeded.
   */
  getRoom(): Room | null {
    return this.session?.getRoom() ?? null;
  }

  async start(): Promise<void> {
    if (this.session) {
      console.log("[voice] start: already running");
      return;
    }

    console.log(`[voice] start: zone=${this.zoneId} instance=${this.instance}`);

    this.localStream = await openMic(MicStore.snapshot.activeMicId);
    // Open mic muted; PTT (startSpeaking) flips it on.
    this.setMicEnabled(false);
    this.attachAnalyser(this.localStream);
    console.log("[voice] mic acquired (muted)");

    const topic = `voice:${this.zoneId}:${this.instance}`;
    const channel = this.socket.channel(topic, {});

    const joinReply = await new Promise<JoinReply>((resolve, reject) => {
      channel
        .join()
        .receive("ok", (r: JoinReply) => resolve(r))
        .receive("error", (err) => reject(new Error(`voice join: ${JSON.stringify(err)}`)))
        .receive("timeout", () => reject(new Error("voice join timeout")));
    });

    console.log(`[voice] channel joined: ${topic} user=${joinReply.user_id} audio_impl=${joinReply.audio_impl}`);

    this.channel = channel;

    // Server emits voice_state to every voice-channel subscriber on
    // voice_active / voice_inactive intents. PlayerList uses this to
    // draw the 🎤 broadcasting icon zone-wide — even for speakers the
    // listener is out of range of (and therefore not subscribed to via
    // LiveKit). The speaker also receives their own state since
    // they're subscribed to the topic.
    channel.on("voice_state", (push: VoiceStatePush) => {
      this.onVoiceState?.(push.user_id, push.active);
    });

    const { token, ws_url, ttl_seconds } = await fetchLiveKitToken(this.zoneId, this.instance);
    console.log(`[voice] token fetched ttl=${ttl_seconds}s ws=${ws_url}`);

    this.spatialController = new SpatialAudioController();

    this.session = new LiveKitVoiceEngineSession({
      wsUrl: ws_url,
      token,
      localStream: this.localStream,
      onRemoteTrack: (identity, track, publication) => {
        console.log(`[voice] remote track + identity=${identity} source=${publication.source} sid=${publication.trackSid}`);
        this.spatialController?.attachRemote(identity, track, publication);
      },
      onRemoteTrackEnded: (identity) => {
        console.log(`[voice] remote track - identity=${identity}`);
        this.spatialController?.detachRemote(identity);
      },
      onParticipantLeft: (identity) => {
        console.log(`[voice] participant left identity=${identity}`);
        this.spatialController?.detachRemote(identity);
      },
      onError: (err) => console.warn("[voice] livekit error:", err),
    });

    await this.session.connect();
    console.log("[voice] livekit room connected");
    this.scheduleTokenRefresh(ttl_seconds);
  }

  private scheduleTokenRefresh(ttlSeconds: number): void {
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);

    const delayMs = ttlSeconds * 1000 - VoiceConnection.REFRESH_LEAD_MS;
    // Skip refresh for unusually short TTLs — natural expiry will
    // surface as a session disconnect and the user reconnects.
    if (delayMs < 60_000) return;

    this.tokenRefreshTimer = setTimeout(() => {
      void this.refreshToken();
    }, delayMs);
  }

  private async refreshToken(): Promise<void> {
    if (this.refreshing || !this.session) return;
    this.refreshing = true;
    console.log("[voice] token refresh: start");

    try {
      const { token, ttl_seconds } = await fetchLiveKitToken(this.zoneId, this.instance);
      await this.session.reconnect(token);
      console.log(`[voice] token refresh: applied, new ttl=${ttl_seconds}s`);
      this.scheduleTokenRefresh(ttl_seconds);
    } catch (err) {
      // Don't reschedule — the existing token still has the lead-time
      // window remaining, and a hard expiry will surface as a Room
      // disconnect for the user to reconnect from.
      console.warn("[voice] token refresh failed:", err);
    } finally {
      this.refreshing = false;
    }
  }

  async stop(): Promise<void> {
    console.log(`[voice] stop: zone=${this.zoneId} instance=${this.instance}`);

    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    if (this.session) {
      await this.session.disconnect().catch(() => {});
      this.session = null;
    }

    this.spatialController?.destroy();
    this.spatialController = null;

    if (this.channel) {
      try {
        await new Promise<void>((resolve) => {
          this.channel?.leave().receive("ok", () => resolve()).receive("timeout", () => resolve());
        });
      } catch {
        // ignore
      }
      this.channel = null;
    }

    this.stopMeter();
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
    this.analyser = null;

    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
  }

  /**
   * Toggle outbound mic publish. PTT press → publishTrack on the
   * LiveKit Room; PTT release → unpublishTrack. The local
   * `track.enabled` flip is kept so the meter stops reading the mic
   * while it's idle.
   */
  setMicEnabled(enabled: boolean): void {
    if (this.localStream) {
      for (const track of this.localStream.getAudioTracks()) {
        track.enabled = enabled;
      }
    }

    if (!this.session) return;
    if (enabled) {
      void this.session.startPtt();
    } else {
      void this.session.stopPtt();
    }
  }

  /**
   * Push voice_start intent and start publishing. Unmutes the local
   * track, starts the meter ticking, and pushes `voice_start` on the
   * channel. No-op until channel is joined.
   */
  startSpeaking(mode: VoiceMode, target: string | null = null): void {
    if (!this.channel) {
      console.log("[voice] startSpeaking: no channel — ignored");
      return;
    }
    if (this.speaking) return;
    this.speaking = true;
    this.currentMode = mode;

    this.setMicEnabled(true);
    this.startMeter();

    const payload: { mode: VoiceMode; target?: string } = { mode };
    if (mode === "whisper" && target) payload.target = target;
    console.log(`[voice] PTT down mode=${mode}${target ? ` target=${target}` : ""}`);
    this.channel.push("voice_start", payload);
  }

  /**
   * Push voice_stop intent and stop publishing. Mutes the local track,
   * stops the meter, and pushes `voice_stop`. No-op if not currently
   * speaking.
   */
  stopSpeaking(): void {
    if (!this.channel) return;
    if (!this.speaking) return;
    this.speaking = false;

    this.setMicEnabled(false);
    this.stopMeter();
    this.setVadSpeaking(false);
    this.onLevel(0);

    console.log("[voice] PTT up");
    this.channel.push("voice_stop", {});
  }

  // ── Local capture meter ────────────────────────────────────────────────

  private attachAnalyser(stream: MediaStream): void {
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);

    this.audioCtx = ctx;
    this.analyser = analyser;
  }

  private startMeter(): void {
    if (this.meterTimer !== null) return;
    if (!this.analyser) return;

    const buf = new Float32Array(this.analyser.fftSize);

    this.meterTimer = window.setInterval(() => {
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(buf);

      let sumSq = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] ?? 0;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / buf.length);

      this.onLevel(Math.min(1, rms * 4)); // scaled for visual punch
      this.setVadSpeaking(rms > VAD_THRESHOLD);
    }, METER_INTERVAL_MS);
  }

  private stopMeter(): void {
    if (this.meterTimer !== null) {
      window.clearInterval(this.meterTimer);
      this.meterTimer = null;
    }
  }

  private setVadSpeaking(next: boolean): void {
    if (next === this.vadSpeaking) return;
    this.vadSpeaking = next;
    this.onSpeakingChange(next, this.currentMode);
  }
}

async function fetchLiveKitToken(
  zoneId: string,
  instance: string,
): Promise<LiveKitTokenReply> {
  // Absolute URL so the request hits the Phoenix API origin, not the
  // Cloudflare Pages origin that serves the client bundle. Authn uses
  // `Authorization: Bearer <jwt>` — same pattern as PlayerList /
  // StatsPanel / every other API call. Session cookies are SameSite=Lax,
  // so they don't ride cross-origin POSTs.
  const res = await fetch(`${config.apiUrl}/api/v1/voice/token`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${activeJwt}`,
    },
    body: JSON.stringify({ zone: zoneId, instance }),
  });

  if (!res.ok) {
    throw new Error(`voice/token: ${res.status} ${await res.text()}`);
  }

  return (await res.json()) as LiveKitTokenReply;
}
